import { action, computed } from "@ember/object"
import Component from "@glimmer/component"
import { A, pipe } from "@mobily/ts-belt"
import type { OnboardingGoal, User } from "@shared/models/user"
import fade from "ember-animated/transitions/fade"
import { includes, isEmpty } from "lodash-es"
import styles from "./goals.module.css"

interface Sig {
  Args: {
    updateField: (field: string, value: unknown) => void
    user: User
    next: () => void
  }
}

/**
 * The onboarding goals for a particular education role, grouped by section.
 */
type OnboardingGoalSections = Array<{
  /** The title to display for this section. */
  title: string
  /** The goals within this section. */
  goals: Array<{
    /**
     * The id to use for this goal in the backend DB.
     * Must be unique across _all_ sections, but can be duplicated
     * across education roles.
     *
     * If you add a new ID option, you must also add it to the OnboardingGoal
     * type in app/models/user.ts (else TypeScript will error).
     */
    id: OnboardingGoal
    /** The text to display for this goal. */
    text: string
  }>
}>

const TEACHER_GOALS: OnboardingGoalSections = [
  {
    title: "I write plans and want to...",
    goals: [
      // {
      //   id: "TEACHER_LEARN_HOW_TO_PLAN",
      //   text: "Learn how to plan",
      // },
      // {
      //   id: "TEACHER_PLAN_FASTER",
      //   text: "Plan faster",
      // },
      {
        id: "TEACHER_ADD_STANDARDS",
        text: "Search & add standards",
      },
      {
        id: "TEACHER_ATTACH_FILES",
        text: "Add links & files to plans",
      },
      // {
      //   id: "TEACHER_MANAGE_MY_SCHEDULE",
      //   text: "Manage my schedule",
      // },
      {
        id: "TEACHER_LESSON_PLANNING",
        text: "Create lesson plans",
      },
      {
        id: "TEACHER_UNIT_PLANNING",
        text: "Create unit plans",
      },
      // {
      //   id: "ALL_MATERIALS_IN_ONE_PLACE",
      //   text: "Have all files and plans into one place",
      // },
      {
        id: "TEACHER_TRACK_STANDARDS",
        text: "Track standards",
      },
      //   ],
      // },
      // {
      //   title: "Make my admins happy",
      //   goals: [
      {
        id: "TEACHER_SUBMIT_PLANS",
        text: "Submit plans to my admin",
      },
      {
        id: "TEACHER_FILL_OUT_TEMPLATE",
        text: "Fill out a required lesson plan template",
      },
    ],
  },
  {
    title: "I want to collaborate and...",
    goals: [
      {
        id: "TEACHER_PLAN_WITH_COTEACHER",
        text: "Plan with a co-teacher",
      },
      {
        id: "TEACHER_PLAN_WITH_TEACHER_IN_DIFFERENT_SCHOOL",
        text: "Plan with a teacher I know in another school",
      },
    ],
  },
  {
    title: "I want to share with students and...",
    goals: [
      {
        id: "TEACHER_POST_TO_CLASS_WEBSITE",
        text: "Post plans to a class website",
      },
      {
        id: "TEACHER_POST_TO_GOOGLE_CLASSROOM",
        text: "Post plans to Google Classroom",
      },
    ],
  },
  {
    title: "I'm required to use this...",
    goals: [
      {
        id: "TEACHER_REQUIRED_AND_NOT_HAPPY",
        text: "And I'm not happy about it.",
      },
      {
        id: "TEACHER_REQUIRED_AND_AMBIVALENT",
        text: "And maybe it will be okay?",
      },
      {
        id: "TEACHER_REQUIRED_AND_EXCITED",
        text: "And I'm excited!",
      },
    ],
  },
  {
    title: "",
    goals: [
      {
        id: "TEACHER_OTHER",
        text: "Other",
      },
    ],
  },
]

const ADMIN_GOALS: OnboardingGoalSections = [
  {
    title: "Getting my staff to plan...",
    goals: [
      {
        id: "SCHOOL_ADMIN_PLAN_CONSISTENTLY",
        text: "consistently!",
      },
      {
        id: "SCHOOL_ADMIN_SCHOOL_WIDE_TEMPLATE",
        text: "using a school wide template(s)",
      },
      {
        id: "SCHOOL_ADMIN_TEACHER_COPLAN",
        text: "with each other (co-planning)",
      },
    ],
  },
  {
    title: "Review Lessons",
    goals: [
      {
        id: "SCHOOL_ADMIN_CHECK_PLAN_COMPLETION",
        text: "Check teachers' plans",
      },
      {
        id: "SCHOOL_ADMIN_PROVIDE_FEEDBACK",
        text: "Provide feedback on plans",
      },
      {
        id: "SCHOOL_ADMIN_MANAGE_INFORMAL_OBSERVATIONS",
        text: "Manage informal observations",
      },
    ],
  },
  {
    title: "Collaboration",
    goals: [
      {
        id: "SCHOOL_ADMIN_PLC",
        text: "Support PLC collaborative planning",
      },
      {
        id: "SCHOOL_ADMIN_SPED_ESOL_COPLAN",
        text: "Enable SPED/ESOL to co-plan with gen. ed. teachers",
      },
      {
        id: "SCHOOL_ADMIN_CROSS_GRADE_LEVEL",
        text: "Facilitate cross-grade level planning",
      },
    ],
  },
  {
    title: "Management",
    goals: [
      {
        id: "SCHOOL_ADMIN_LESSON_BANK",
        text: "Create a bank of plans to be given to new teachers",
      },
      {
        id: "SCHOOL_ADMIN_SUB_PLANS",
        text: "Manage sub plans",
      },
      {
        id: "SCHOOL_ADMIN_SCHOOL_WIDE_EVENTS",
        text: "Share school wide events (PD Days, meetings, etc)",
      },
      {
        id: "SCHOOL_ADMIN_STANDARDS_USAGE",
        text: "Report on standards usage",
      },
    ],
  },
  {
    title: "",
    goals: [
      {
        id: "SCHOOL_ADMIN_OTHER",
        text: "Other",
      },
    ],
  },
]

const DISTRICT_ADMIN_GOALS: OnboardingGoalSections = [
  {
    title: "Sharing...",
    goals: [
      {
        id: "DISTRICT_ADMIN_SHARING_PACING_GUIDES",
        text: "pacing guides",
      },
      {
        id: "DISTRICT_ADMIN_SHARING_CURRICULUM_RESOURCES",
        text: "curriculum resources",
      },
      {
        id: "DISTRICT_ADMIN_SHARING_CURRICULUM_WITH_PARENTS",
        text: "curriculum guides with parents",
      },
    ],
  },
  {
    title: "Creating & Collaborating on...",
    goals: [
      {
        id: "DISTRICT_ADMIN_CREATING_PACING_GUIDES",
        text: "pacing guides",
      },
      {
        id: "DISTRICT_ADMIN_CREATING_CURRICULUM_RESOURCES",
        text: "district created units and lessons",
      },
    ],
  },
  {
    title: "Centralization",
    goals: [
      {
        id: "DISTRICT_ADMIN_SINGLE_PLACE_FOR_PLANS",
        text: "Create a place for plans across all sites",
      },
      {
        id: "DISTRICT_ADMIN_SINGLE_FORMAT_FOR_PACING",
        text: "Unify pacing guide distribution",
      },
      // {
      //   id: "DISTRICT_ADMIN_UNIFIED_WAY_FOR_PRINCIPALS_TO_CHECK_PLANS",
      //   text: "Establish a unified way for principals to check plans",
      // },
      {
        id: "DISTRICT_ADMIN_BANK_OF_LESSONS_AND_UNITS",
        text: "Create a bank of lessons & units",
      },
    ],
  },
  {
    title: "Tracking & Data",
    goals: [
      {
        id: "DISTRICT_ADMIN_TRACK_PACING",
        text: "Track how many teachers are on pace",
      },
      {
        id: "DISTRICT_ADMIN_TRACK_STANDARDS_USAGE",
        text: "Track standards usage",
      },
      {
        id: "DISTRICT_ADMIN_GENERATE_REPORTS_FOR_AUDITS",
        text: "Create reports for audits/accreditation",
      },
    ],
  },
  {
    title: "",
    goals: [
      {
        id: "DISTRICT_ADMIN_OTHER",
        text: "Other",
      },
    ],
  },
]

const OTHER_GOALS: OnboardingGoalSections = []

export const VALID_GOAL_ROLES = ["teacher", "admin", "district-admin"]

export default class GoalsComponent extends Component<Sig> {
  readonly styles = styles
  fadeTransition = fade

  @computed("args.user.attributes.onboardingGoals")
  get canShowNext() {
    return !isEmpty(this.args.user.attributes.onboardingGoals) || isEmpty(this.sections)
  }

  get sections(): OnboardingGoalSections {
    if (!this.args.user) return []

    const role = this.args.user.attributes.educationRole
    switch (role) {
      case "teacher":
        return TEACHER_GOALS
      case "admin":
        return ADMIN_GOALS
      case "district-admin":
        return DISTRICT_ADMIN_GOALS
      default:
        console.log("Failed to skip goals step for role", role)
        return OTHER_GOALS
    }
  }

  @action toggleSelected(id: OnboardingGoal) {
    let isSelected = includes(this.args.user.attributes.onboardingGoals, id)
    // Update directly in the store.
    this.args.updateField(
      "onboardingGoals",
      pipe(
        this.args.user.attributes.onboardingGoals,
        isSelected ? A.reject((goal) => goal === id) : A.append(id)
      )
    )
  }
}
